/**
 * Module dependencies.
 */

import React from 'react';

/**
 * `Props` type.
 */

type Props = {
  responseStatus: string;
  responseBody: string;
  error: string | null;
};

/**
 * Get message based on status code.
 */

function getStatusMessage(status: string, responseBody: string): string {
  const statusCode = parseInt(status, 10);

  // Success responses
  if (statusCode >= 200 && statusCode < 300) {
    try {
      const body = JSON.parse(responseBody);
      const totalResults = body?.results?.length || body?.total || 0;

      if (totalResults === 0) {
        return 'Request successful - No therapies found';
      }

      return `Request successful - ${totalResults} ${totalResults === 1 ? 'therapy' : 'therapies'} retrieved`;
    } catch {
      return 'Request successful';
    }
  }

  // Redirection responses
  if (statusCode >= 300 && statusCode < 400) {
    return `Redirect response - ${statusCode}`;
  }

  // Client error responses
  if (statusCode >= 400 && statusCode < 500) {
    if (statusCode === 400) {
      return 'Bad Request - Invalid request parameters';
    }

    if (statusCode === 401) {
      return 'Unauthorized - Authentication required';
    }

    if (statusCode === 403) {
      return 'Forbidden - Access denied';
    }

    if (statusCode === 404) {
      return 'Not Found - Resource does not exist';
    }

    if (statusCode === 409) {
      try {
        const body = JSON.parse(responseBody);
        const errorCode = body?.data?.code || 'conflict';
        return `Conflict - ${errorCode.replace(/_/g, ' ')}`;
      } catch {
        return 'Conflict - Duplicate or conflicting resource';
      }
    }

    if (statusCode === 422) {
      return 'Unprocessable Entity - Validation failed';
    }

    if (statusCode === 429) {
      return 'Too Many Requests - Rate limit exceeded';
    }

    return `Client Error - ${statusCode}`;
  }

  // Server error responses
  if (statusCode >= 500) {
    if (statusCode === 500) {
      return 'Internal Server Error - Something went wrong';
    }

    if (statusCode === 502) {
      return 'Bad Gateway - Upstream server error';
    }

    if (statusCode === 503) {
      return 'Service Unavailable - Server temporarily unavailable';
    }

    if (statusCode === 504) {
      return 'Gateway Timeout - Request timeout';
    }

    return `Server Error - ${statusCode}`;
  }

  return `Response - ${statusCode}`;
}

/**
 * Get color based on status code.
 */

function getStatusColor(status: string): string {
  const statusCode = parseInt(status, 10);

  if (statusCode >= 200 && statusCode < 300) {
    return 'inherit'; // black (default text color)
  }

  if (statusCode >= 300 && statusCode < 400) {
    return '#1890ff'; // blue
  }

  if (statusCode >= 400 && statusCode < 500) {
    return '#faad14'; // orange
  }

  if (statusCode >= 500) {
    return '#ff4d4f'; // red
  }

  return 'inherit';
}

/**
 * Export `TherapyBodyRenderer` component.
 */

export function TherapyBodyRenderer({ responseStatus, responseBody }: Props): JSX.Element {
  const message = getStatusMessage(responseStatus, responseBody);
  const color = getStatusColor(responseStatus);

  return <span style={{ fontSize: '13px', color }}>{message}</span>;
}
